import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Componente que:
 * - Consulta el estado de la licencia del usuario logueado
 * - Si vencida: muestra pantalla bloqueante con info de contacto
 *   EXCEPTO si el usuario está en /pagar (para que pueda renovar)
 * - Si por vencer (<=2 días): muestra banner superior de advertencia
 */
export default function AvisoLicencia({ children }) {
  const { logout } = useAuth();
  const location = useLocation();
  const [licencia, setLicencia] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      try {
        const { data } = await api.get('/demo/estado-licencia');
        setLicencia(data);
      } catch {
        setLicencia(null);
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [location.pathname]); // re-checar al cambiar de ruta

  if (cargando) return children;

  // ✅ RUTAS PERMITIDAS aunque la licencia esté vencida
  const rutasPermitidasVencida = ['/pagar', '/ayuda', '/descargar-app'];
  const puedeNavegar = rutasPermitidasVencida.some((p) =>
    location.pathname.startsWith(p)
  );

  // ─── Caso 1: Licencia VENCIDA → bloqueo total (excepto rutas permitidas) ───
  if (licencia?.es_demo && licencia?.vencida && !puedeNavegar) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center border-4 border-red-500">
          <div className="text-6xl mb-4">⏰</div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">
            Tu prueba gratuita ha finalizado
          </h1>
          <p className="text-sm text-slate-600 mb-6 leading-relaxed">
            Tu período de prueba de 7 días ha terminado.
            <br />
            Puedes renovar tu plan ahora mismo subiendo tu comprobante de pago,
            o contactarnos directamente.
          </p>

          {/* ✅ BOTÓN RENOVAR DESTACADO */}
          <button
            onClick={() => {
              window.location.href = '/pagar';
            }}
            className="w-full py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 font-bold text-lg mb-4 shadow-lg transition"
          >
            💳 Renovar mi plan ahora
          </button>

          <div className="bg-blue-50 rounded-xl p-5 mb-4 text-left space-y-2">
            <div className="text-xs font-bold text-blue-900 mb-2">
              📞 ¿Prefieres contactarnos?
            </div>
            <a
              href="https://wa.me/593981560147"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-white rounded-lg hover:bg-green-50 transition border border-green-200"
            >
              <div className="text-2xl">💬</div>
              <div className="flex-1">
                <div className="text-xs text-slate-500">WhatsApp</div>
                <div className="font-bold text-slate-800">+593 98 156 0147</div>
              </div>
              <div className="text-green-600 text-sm">→</div>
            </a>
            <a
              href="mailto:Ventas@lluchunga.com"
              className="flex items-center gap-3 p-3 bg-white rounded-lg hover:bg-blue-50 transition border border-blue-200"
            >
              <div className="text-2xl">📧</div>
              <div className="flex-1">
                <div className="text-xs text-slate-500">Email</div>
                <div className="font-bold text-slate-800">Ventas@lluchunga.com</div>
              </div>
              <div className="text-blue-600 text-sm">→</div>
            </a>
          </div>

          <button
            onClick={logout}
            className="w-full py-2 text-sm text-slate-500 hover:text-slate-700 underline"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    );
  }

  // ─── Caso 2: Por vencer (<=2 días) → banner + contenido ───
  const mostrarBanner =
    licencia?.es_demo && licencia?.por_vencer && !licencia?.vencida;

  return (
    <>
      {mostrarBanner && (
        <div className="bg-amber-400 text-amber-900 px-4 py-2 text-xs flex flex-wrap items-center justify-center gap-3 border-b-2 border-amber-500">
          <span className="text-base">⚠️</span>
          <span className="font-medium">
            Tu prueba gratuita vence en{' '}
            <strong>
              {licencia.dias_restantes} día
              {licencia.dias_restantes !== 1 ? 's' : ''}
            </strong>
            .
          </span>
          <a
            href="/pagar"
            className="px-3 py-1 bg-amber-900 text-white rounded-full text-[11px] hover:bg-amber-800 font-semibold"
          >
            💳 Pagar ahora
          </a>
          <span className="text-[11px] opacity-75 hidden sm:inline">
            o contacta: <strong>+593 98 156 0147</strong>
          </span>
        </div>
      )}
      {children}
    </>
  );
}